// CHG-1657 / ITER-3766 (W3.2) — pure-TS helpers for the unified envelope
// emitted by /api/v1/public/widget/{widgetId}/options + /donate-pages/
// public-by-id/{id} (envelope unified by CHG-1637 / W2.2).
//
// Server-side authoritative gate lives at
//   src/api/app/services/render_constraints.py:resolve_render_constraints
//   (ref:tier-3-calculation/render-constraints-resolution)
// Server route enforces the gate at
//   src/api/app/routers/public_donations.py (CHG-1459)
//
// This module is the CLIENT MIRROR — purely defensive UX. We never trust
// the client; the server rejects any submission that violates the gate.
// See feedback_fe_be_validation_must_match (mirror, don't loosen).
//
// Spec contract:
//   ref:tier-4-rest-api-spec/embed-widget#WidgetOptionsResponse
//   ref:tier-4-rest-api-spec/donate-page#PageContextRead
//   ref:tier-4-rest-api-spec/donate-page#RenderConstraintsRead

/** Shape of `envelope.page_context` per backend `_synthesize_widget_page_context`. */
export interface EnvelopePageContext {
  context_type: string;
  subtype: string | null;
  entity_id: string | null;
  organization_id: string;
  parent_chain: string[];
  brand_variant_id: string | null;
  locale: string | null;
}

/** One row in `envelope.constraints.allowed_designations`. */
export interface EnvelopeAllowedDesignation {
  id: string;
  display_name: string;
  designation_type: string | null;
  parent_chain: string[];
}

/** `envelope.constraints.amount_bounds`. */
export interface EnvelopeAmountBounds {
  min_minor_units: number | null;
  max_minor_units: number | null;
  suggested_amounts: number[];
}

/** `envelope.constraints` block. */
export interface EnvelopeConstraints {
  allowed_designations: EnvelopeAllowedDesignation[];
  campaign_lock: string | null;
  amount_bounds: EnvelopeAmountBounds;
  // donate-page-shape provider capability map (e.g. {stripe: true, paypal: false}).
  // Widget surface ALSO exposes capability tokens (recurring/one_time/...)
  // at envelope.org_provider_capabilities — both are populated by the server
  // and consumers should prefer this field for per-method gating.
  provider_capabilities: Record<string, boolean> | null;
  recurring_allowed: boolean;
  pledge_allowed: boolean;
  off_season_action: 'accept' | 'redirect' | 'reject';
  off_season_redirect_url: string | null;
}

/** `envelope.context_lock` — null when no entity is pre-bound. */
export interface EnvelopeContextLock {
  kind: 'designation' | 'campaign';
  id: string;
  display_name: string;
}

/** Top-level envelope shape from the widget /options endpoint OR donate-page
 *  public-by-id endpoint. Both surfaces ship the same shape post-CHG-1637. */
export interface UnifiedEnvelope {
  page_context: EnvelopePageContext;
  constraints: EnvelopeConstraints;
  context_lock: EnvelopeContextLock | null;
}

// ── context_lock helpers ──────────────────────────────────────────────────

/** When non-null, the donor is pre-bound to a designation OR campaign and
 *  the designation picker must NOT be rendered. */
export function hasContextLock(envelope: UnifiedEnvelope | null | undefined): boolean {
  return envelope?.context_lock != null;
}

/** Returns the locked target or null. Convenience accessor. */
export function getContextLock(
  envelope: UnifiedEnvelope | null | undefined,
): EnvelopeContextLock | null {
  return envelope?.context_lock ?? null;
}

/** Single-line confirmation text the widget renders in place of the picker
 *  when a context_lock is in effect. The "Donating to {display_name}" prefix
 *  is a fixed string today; future i18n moves it through the widget's
 *  string table. */
export function formatLockedTargetConfirmation(
  lock: EnvelopeContextLock | null | undefined,
  prefix = 'Donating to',
): string {
  if (!lock) return '';
  const name = (lock.display_name || '').trim();
  return name ? `${prefix} ${name}` : 'Donation target confirmed';
}

// ── allowed_designations helpers ─────────────────────────────────────────

/** Returns true when `allowed_designations` is set and constrains the
 *  picker. An empty list means "no restriction" — the widget shows the
 *  full set of donor-visible designations for the org. (Mirrors server:
 *  RenderConstraints.allowed_designation_ids empty == unconstrained.) */
export function isDesignationConstrained(
  envelope: UnifiedEnvelope | null | undefined,
): boolean {
  const list = envelope?.constraints?.allowed_designations;
  return Array.isArray(list) && list.length > 0;
}

/** Project allowed-designation IDs as a Set for fast membership tests. */
export function getAllowedDesignationIdSet(
  envelope: UnifiedEnvelope | null | undefined,
): Set<string> {
  const list = envelope?.constraints?.allowed_designations ?? [];
  return new Set(list.map((d) => d.id));
}

/** Filter a candidate designation list down to envelope-allowed entries.
 *  If `allowed_designations` is empty, returns the input unchanged
 *  (unconstrained semantics). */
export function filterAllowedDesignations<T extends { id: string }>(
  designations: T[],
  envelope: UnifiedEnvelope | null | undefined,
): T[] {
  if (!isDesignationConstrained(envelope)) return designations;
  const ids = getAllowedDesignationIdSet(envelope);
  return designations.filter((d) => ids.has(d.id));
}

// ── amount_bounds helpers ────────────────────────────────────────────────

/** Effective min cents from the envelope's amount_bounds; null when
 *  envelope didn't declare one (caller falls back to legacy widget config). */
export function getMinMinorUnits(envelope: UnifiedEnvelope | null | undefined): number | null {
  const b = envelope?.constraints?.amount_bounds;
  if (!b) return null;
  return typeof b.min_minor_units === 'number' ? b.min_minor_units : null;
}

/** Effective max cents from envelope; null when unbounded. */
export function getMaxMinorUnits(envelope: UnifiedEnvelope | null | undefined): number | null {
  const b = envelope?.constraints?.amount_bounds;
  if (!b) return null;
  return typeof b.max_minor_units === 'number' ? b.max_minor_units : null;
}

/** Envelope-declared suggested amounts in cents. Empty array when
 *  envelope didn't ship any (caller falls back). */
export function getSuggestedAmountsMinorUnits(
  envelope: UnifiedEnvelope | null | undefined,
): number[] {
  const arr = envelope?.constraints?.amount_bounds?.suggested_amounts;
  return Array.isArray(arr) ? arr.slice() : [];
}

/** Validate an amount (in cents) against the envelope's amount_bounds.
 *  Returns null when valid, or an error code identifying the failure. */
export type AmountValidationError = 'too_low' | 'too_high';

export function validateAmountMinorUnits(
  amountMinorUnits: number,
  envelope: UnifiedEnvelope | null | undefined,
): AmountValidationError | null {
  const min = getMinMinorUnits(envelope);
  const max = getMaxMinorUnits(envelope);
  if (min != null && amountMinorUnits < min) return 'too_low';
  if (max != null && amountMinorUnits > max) return 'too_high';
  return null;
}

// ── recurring_allowed / pledge_allowed helpers ───────────────────────────

/** Recurring gate. Returns true when the envelope did NOT explicitly disable
 *  recurring (default-permissive on missing/null envelope so legacy renders
 *  keep working). */
export function isRecurringAllowed(envelope: UnifiedEnvelope | null | undefined): boolean {
  return envelope?.constraints?.recurring_allowed !== false;
}

/** Pledge gate. Same default-permissive semantics. */
export function isPledgeAllowed(envelope: UnifiedEnvelope | null | undefined): boolean {
  return envelope?.constraints?.pledge_allowed !== false;
}

// ── off_season_action helpers ────────────────────────────────────────────

export type OffSeasonDirective =
  | { kind: 'accept' }
  | { kind: 'redirect'; url: string }
  | { kind: 'reject' };

/** Resolve the off-season directive from the envelope. */
export function getOffSeasonDirective(
  envelope: UnifiedEnvelope | null | undefined,
): OffSeasonDirective {
  const action = envelope?.constraints?.off_season_action;
  if (action === 'redirect') {
    const url = envelope?.constraints?.off_season_redirect_url;
    // Redirect with a missing URL is a server-side misconfiguration. We
    // fall back to 'accept' so the donor sees the normal form rather than
    // a blank/broken redirect; the operator banner surfaces this as a
    // dashboard warning (DRIFT-602 / future work).
    if (typeof url === 'string' && url.length > 0) {
      return { kind: 'redirect', url };
    }
    return { kind: 'accept' };
  }
  if (action === 'reject') return { kind: 'reject' };
  return { kind: 'accept' };
}

// ── provider_capabilities helpers ────────────────────────────────────────

/** A payment-method-shaped row the widget renders. Matches
 *  PaymentMethodEntry from api.ts but redeclared locally so this helper
 *  module stays import-free. */
export interface ProviderShape {
  provider_key: string;
}

/** Filter payment methods to those the envelope's `provider_capabilities`
 *  map says the org has capability for.
 *
 *  Defaults:
 *    - `provider_capabilities` is null → unconstrained (pre-resolver-step-3
 *      shape; widget falls back to `org_provider_capabilities`). Returns
 *      input unchanged.
 *    - method missing from the map → kept (permissive on unknown keys per
 *      provider-adapter-contract §T10.4).
 *    - method present with explicit `false` → dropped.
 */
export function filterPaymentMethodsByCapabilities<T extends ProviderShape>(
  methods: T[],
  envelope: UnifiedEnvelope | null | undefined,
): T[] {
  const caps = envelope?.constraints?.provider_capabilities;
  if (caps == null) return methods;
  return methods.filter((m) => caps[m.provider_key] !== false);
}

// ── submission gate (mirror of server-side CHG-1459) ─────────────────────

export interface SubmissionValidationInput {
  envelope: UnifiedEnvelope | null | undefined;
  designationId: string | null | undefined;
  campaignId: string | null | undefined;
  amountMinorUnits: number;
}

export type SubmissionValidationError =
  | 'target_required'
  | 'context_lock_mismatch'
  | 'designation_not_allowed'
  | 'amount_too_low'
  | 'amount_too_high'
  | 'off_season_closed';

/** Mirror of the server-side gate (ref:tier-3-calculation/
 *  render-constraints-resolution + CHG-1459 enforcement in
 *  src/api/app/routers/public_donations.py). Returns null when the
 *  submission would pass; otherwise the first failure code.
 *
 *  Order matters — context_lock is checked first because it short-circuits
 *  allowed_designations + campaign selection. */
export function validateSubmission(
  input: SubmissionValidationInput,
): SubmissionValidationError | null {
  const { envelope, designationId, campaignId, amountMinorUnits } = input;
  if (!designationId && !campaignId) return 'target_required';
  if (!envelope) return null; // legacy callers (no envelope yet) skip the mirror

  // Off-season reject blocks ALL submissions regardless of amount/target.
  const off = getOffSeasonDirective(envelope);
  if (off.kind === 'reject') return 'off_season_closed';

  const lock = getContextLock(envelope);
  if (lock) {
    if (lock.kind === 'designation') {
      if (designationId !== lock.id) return 'context_lock_mismatch';
    } else if (lock.kind === 'campaign') {
      if (campaignId !== lock.id) return 'context_lock_mismatch';
    }
  } else if (isDesignationConstrained(envelope)) {
    // Only enforce allowed_designations when there's no lock (lock supersedes).
    if (designationId) {
      const allowed = getAllowedDesignationIdSet(envelope);
      if (!allowed.has(designationId)) return 'designation_not_allowed';
    }
  }

  const amountErr = validateAmountMinorUnits(amountMinorUnits, envelope);
  if (amountErr === 'too_low') return 'amount_too_low';
  if (amountErr === 'too_high') return 'amount_too_high';

  return null;
}
